import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { escapeRegExp } from "lodash";

import { durationFormatHandlerWithHours } from "../../../components/common/duration";

export const getHighlightedParagraphs = (paragraphs, searchTerm, foundWordsCount) => {
  if (!paragraphs || paragraphs.length === 0) {
    return { newPar: [], count: 0 };
  }

  if (!searchTerm) {
    return { newPar: paragraphs, count: 0 };
  }

  const searchValueArray = searchTerm.trim().split(" ");
  let count = foundWordsCount;
  let allMatch = [];

  const newPar = paragraphs.map((part) => {
    const { children, ...rest } = part;
    const words = children[0].words;
    let offsetPosition = 0;
    let wordsIndexes = [];
    let currentSearchTermIndex = 0;
    const arrayWithSearchFounded = [];

    const parts = words.flatMap((word, wordIndex) => {
      const searchTermEscaped = escapeRegExp(searchValueArray[currentSearchTermIndex]);
      const regex = new RegExp(`(${searchTermEscaped})`, "gi");
      const matchArray = word.text.split(regex);
      const endOfWord = matchArray.length - 1;
      if (matchArray.length > 1) {
        offsetPosition += matchArray.length - 1;
      }

      const foundWord = matchArray.map((text, index) => {
        const basePart = {
          text,
          isMatch: false,
          index: null,
          start: word.start,
          end: word.end,
          endOfWord: endOfWord === index,
        };
        if (index % 2 === 1) {
          arrayWithSearchFounded.push(matchArray);

          // escape condition: it's only 1 search term
          if (searchValueArray.length === 1) {
            return {
              ...basePart,
              isMatch: true,
              // eslint-disable-next-line no-plusplus
              index: count++,
            };
          }
          // escape condition: match is in the first half of the word and still remaining search terms to compare
          if (matchArray[index + 1] !== "" && searchValueArray[currentSearchTermIndex + 1]) {
            currentSearchTermIndex = 0;
            wordsIndexes = [];

            return basePart;
          }
          // first match
          if (currentSearchTermIndex === 0) {
            const offset = arrayWithSearchFounded.length === 1 ? 1 : offsetPosition - matchArray.length + 2;
            wordsIndexes.push(wordIndex + offset);
            // eslint-disable-next-line no-plusplus
            currentSearchTermIndex++;
            return basePart;
          }
          if (currentSearchTermIndex < searchValueArray.length - 1) {
            wordsIndexes.push(wordIndex + offsetPosition - 1);
            // eslint-disable-next-line no-plusplus
            currentSearchTermIndex++;
            return basePart;
          }
          // full match
          if (currentSearchTermIndex === searchValueArray.length - 1) {
            wordsIndexes.push(wordIndex + offsetPosition - 1);
            allMatch.push({ indexes: wordsIndexes, count });
            currentSearchTermIndex = 0;
            // eslint-disable-next-line no-plusplus
            count++;
            wordsIndexes = [];
            return basePart;
          }
        }
        // the next word it's not a match
        if (text !== "" && currentSearchTermIndex > 0) {
          wordsIndexes = [];
          currentSearchTermIndex = 0;
        }
        return basePart;
      });
      return foundWord;
    });

    allMatch.forEach((coincidence) => {
      coincidence.indexes.forEach((wordIndex) => {
        parts[wordIndex] = {
          ...parts[wordIndex],
          isMatch: true,
          index: coincidence.count,
        };
      });
    });
    allMatch = [];
    return { parts, ...rest };
  });

  return { newPar, count };
};

export const getHighlightedChapters = (chapter, searchTerm, foundWords) => {
  if (!searchTerm) {
    return {
      title: chapter.title,
      description: chapter.description,
      newPar: chapter.paragraphs,
      count: foundWords,
      keywords: chapter.keywords,
    };
  }

  let count = foundWords;
  const { title, description, paragraphs, keywords } = chapter;
  const searchTermEscaped = escapeRegExp(searchTerm);
  const regex = new RegExp(`(${searchTermEscaped})`, "gi");
  if (!searchTerm) {
    return { newPar: paragraphs, count: 0 };
  }

  const searchValueArray = searchTerm.trim().split(" ");
  let allMatch = [];

  const highlightText = (text) =>
    text.split(regex).map((part, index) => ({
      text: part,
      isMatch: index % 2 === 1,
      // eslint-disable-next-line no-plusplus
      index: index % 2 === 1 ? count++ : null,
    }));

  const highlightedTitle = title ? highlightText(title) : title;
  const highlightedDescription = description ? highlightText(description) : description;
  const highlightedKeywords = keywords ? highlightText(keywords) : keywords;

  const newPar = paragraphs.map((part) => {
    const { children, ...rest } = part;
    const words = children[0].words;
    let offsetPosition = 0;
    let wordsIndexes = [];
    let currentSearchTermIndex = 0;
    const arrayWithSearchFounded = [];

    const parts = words.flatMap((word, wordIndex) => {
      const searchTermEsc = escapeRegExp(searchValueArray[currentSearchTermIndex]);
      const reg = new RegExp(`(${searchTermEsc})`, "gi");
      const matchArray = word.text.split(reg);
      const endOfWord = matchArray.length - 1;

      if (matchArray.length > 1) {
        offsetPosition += matchArray.length - 1;
      }

      const foundWord = matchArray.map((text, index) => {
        const basePart = {
          text,
          isMatch: false,
          index: null,
          start: word.start,
          end: word.end,
          endOfWord: endOfWord === index,
        };
        if (index % 2 === 1) {
          arrayWithSearchFounded.push(matchArray);

          // escape condition: it's only 1 search term
          if (searchValueArray.length === 1) {
            return {
              ...basePart,
              isMatch: true,
              // eslint-disable-next-line no-plusplus
              index: count++,
            };
          }
          // escape condition: match is in the first half of the word and still remaining search terms to compare
          if (matchArray[index + 1] !== "" && searchValueArray[currentSearchTermIndex + 1]) {
            currentSearchTermIndex = 0;
            wordsIndexes = [];

            return basePart;
          }
          // first match
          if (currentSearchTermIndex === 0) {
            const offset = arrayWithSearchFounded.length === 1 ? 1 : offsetPosition - matchArray.length + 2;
            wordsIndexes.push(wordIndex + offset);
            // eslint-disable-next-line no-plusplus
            currentSearchTermIndex++;
            return basePart;
          }
          // a match in the middle
          if (currentSearchTermIndex < searchValueArray.length - 1) {
            wordsIndexes.push(wordIndex + offsetPosition - 1);
            // eslint-disable-next-line no-plusplus
            currentSearchTermIndex++;
            return basePart;
          }
          // full match
          if (currentSearchTermIndex === searchValueArray.length - 1) {
            wordsIndexes.push(wordIndex + offsetPosition - 1);
            allMatch.push({ indexes: wordsIndexes, count });
            currentSearchTermIndex = 0;
            // eslint-disable-next-line no-plusplus
            count++;
            wordsIndexes = [];
            return basePart;
          }
        }
        // the next word it's not a match
        if (text !== "" && currentSearchTermIndex > 0) {
          wordsIndexes = [];
          currentSearchTermIndex = 0;
        }
        return basePart;
      });

      return foundWord;
    });

    allMatch.forEach((coincidence) => {
      coincidence.indexes.forEach((wordIndex) => {
        parts[wordIndex] = {
          ...parts[wordIndex],
          isMatch: true,
          index: coincidence.count,
        };
      });
    });
    allMatch = [];

    return { parts, ...rest };
  });

  return { title: highlightedTitle, description: highlightedDescription, keywords: highlightedKeywords, newPar, count };
};

export const getSecondsToSkip = (timeToSkip) => {
  const durationObject = moment.duration(timeToSkip).asSeconds();
  return durationObject;
};

export const transformIndexFormat = (currentIndexes) => {
  const transformedArray = currentIndexes.map((item) => {
    const metadata = item.metadata.map((meta) => ({
      title: meta.title || item.title,
      description: meta.synopsis,
      keywords: meta.keywords,
      start: meta.time.start,
      end: null,
      _id: uuidv4(),
    }));

    // Case when there isn't any bookmark (empty index). We create one
    if (metadata.length === 0) {
      const firstSection = {
        title: "First Section (auto-generated)",
        description: "",
        start: 0,
        // end: metadata[0].start - 0.5,
        _id: uuidv4(),
      };
      metadata.unshift(firstSection);
    }

    // Case when there is no first section (first bookmark is more than 00:00:00). We create a first section
    if (metadata.length === 0 || (metadata.length > 0 && metadata[0].start > 0)) {
      const firstSection = {
        title: "First Section (auto-generated)",
        description: "",
        start: 0,
        end: metadata[0].start - 0.5,
        _id: uuidv4(),
      };
      metadata.unshift(firstSection);
    }

    metadata.sort((a, b) => a.start - b.start);
    metadata.forEach((meta, index) => {
      if (index < metadata.length - 1) {
        meta.end = metadata[index + 1].start;
      }
    });
    return {
      title: item.title,
      chapters: metadata,
      _id: item._id,
      created_at: item.created_at ?? item.createdAt,
    };
  });

  return transformedArray;
};

export const buildIndexes = (paragraphs, indexesArray) => {
  const newIndexesArray = [...indexesArray];

  newIndexesArray.forEach((chapters) => {
    chapters.chapters.forEach((chapter) => {
      const subParagraphs = getSubParagraphs(paragraphs, chapter.start, chapter.end);
      chapter.paragraphs = subParagraphs;
    });
  });
  return newIndexesArray;
};

const getSubParagraphs = (paragraphs, start, defaultEnd) => {
  let end = defaultEnd;
  const subParagraphs = [];

  if (!end) {
    const lastIndex = paragraphs.length - 1;
    const lastWord = paragraphs[lastIndex].children[0].words.length - 1;
    end = paragraphs[lastIndex].children[0].words[lastWord].end;
  }
  paragraphs.forEach((paragraph, index) => {
    const isLastParagraph = index === paragraphs.length - 1;
    if (!isLastParagraph) {
      if (paragraph.start > end) {
        return;
      }
      if (start >= paragraph.start && start < paragraphs[index + 1].start) {
        const currSubParagraph = getSubParagraph(paragraph, start, end);
        subParagraphs.push(currSubParagraph);
      }
      if (start < paragraph.start && end < paragraphs[index + 1].start) {
        const currSubParagraph = getLastSubParagraph(paragraph, end);

        subParagraphs.push(currSubParagraph);
      }
      if (start < paragraph.start && paragraphs[index + 1].start < end) {
        subParagraphs.push(paragraph);
      }
    }
    if (isLastParagraph) {
      if (end > paragraph.start && start <= paragraph.start) {
        const currSubParagraph = getLastSubParagraph(paragraph, end);
        subParagraphs.push(currSubParagraph);
      }

      if (start > paragraph.start) {
        const currSubParagraph = getSubParagraph(paragraph, start, end);
        subParagraphs.push(currSubParagraph);
      }
    }
  });
  return subParagraphs;
};

const getSubParagraph = (paragraph, start, end) => {
  let subPar = [];
  let newText = "";
  paragraph.children[0].words.forEach((word, i) => {
    if (start >= word.start) {
      subPar = i !== 0 ? paragraph.children[0].words.slice(i + 1) : paragraph.children[0].words.slice(i);
    }
  });

  subPar.forEach((word, i) => {
    if (end < word.start) {
      subPar = subPar.slice(0, i);
      return;
    }
    newText += `${word.text} `;
  });
  const time = subPar[0] && subPar[0].start;
  const transfPara = {
    ...paragraph,
    startTimecode: time && durationFormatHandlerWithHours(time),
    start: time,
    children: [{ words: subPar, text: newText }],
  };
  return transfPara;
};

const getLastSubParagraph = (paragraph, end) => {
  let subPar = [...paragraph.children[0].words];
  let newText = "";
  for (const [i, word] of paragraph.children[0].words.entries()) {
    if (end < word.start) {
      subPar = paragraph.children[0].words.slice(0, i);
      break;
    }
    newText += `${word.text} `;
  }
  const time = subPar[0].start;
  const transfPara = {
    ...paragraph,
    startTimecode: durationFormatHandlerWithHours(time),
    start: time,
    children: [{ words: subPar, text: newText }],
  };
  return transfPara;
};

// The nearest ancestor that actually scrolls (overflow auto/scroll with overflowing content).
const getScrollContainer = (el: HTMLElement): HTMLElement | null => {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) return node;
  }
  return null;
};

/**
 * Scroll a transcript word / search hit into view. The lookup is scoped to `root`
 * (the transcript that owns it) and only the nearest scroll container moves —
 * unlike scrollIntoView, which also scrolls every outer pane (the chat side panel
 * sits inside overflow-hidden page layout). Falls back to scrollIntoView when no
 * scrolling ancestor exists.
 */
export const scrollToCurrentWord = (
  id: string | number,
  { root = document, block = "start" }: { root?: ParentNode; block?: "start" | "center" } = {}
) => {
  const element = root.querySelector<HTMLElement>(`[id="${id}"]`);
  if (!element) return;
  const container = getScrollContainer(element);
  if (!container) {
    element.scrollIntoView({ behavior: "smooth", block });
    return;
  }
  const elRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const offset = elRect.top - containerRect.top + container.scrollTop;
  const top = block === "center" ? offset - container.clientHeight / 2 + elRect.height / 2 : offset;
  container.scrollTo({ top, behavior: "smooth" });
};

export const selectClipText = (idStart, idEnd) => {
  const spanStart = document.getElementById(idStart);
  const spanEnd = document.getElementById(idEnd);

  if (!spanStart || !spanEnd) {
    // id's not found
    return;
  }

  const startNodeText = spanStart.firstChild;
  if (!startNodeText || startNodeText.nodeType !== Node.TEXT_NODE) {
    // not valid text
    return;
  }
  const endNodeText = spanEnd.firstChild;
  if (!endNodeText || endNodeText.nodeType !== Node.TEXT_NODE) {
    // not valid text
    return;
  }

  const range = document.createRange();
  range.setStartAfter(startNodeText);
  range.setEndBefore(endNodeText);
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
};

let lastScrolledWord = 0;

export const selectCurrentWords = (
  playing: boolean,
  words: HTMLCollectionOf<HTMLSpanElement>,
  currentTime: number,
  isAutoScroll: boolean,
  isHighlightWords: boolean
) => {
  if (words.length === 0) return;

  // little adjust on time (0,5) to match words
  const adjustCurrentTime = currentTime + 0.5;

  for (let i = 0; i < words.length; i++) {
    const wordTime = parseFloat(words[i].id);

    if (adjustCurrentTime >= wordTime) {
      words[i].style.color = playing && isHighlightWords ? "#000000de" : "#000000de";
      lastScrolledWord = i;
    } else {
      words[i].style.color = playing && isHighlightWords ? "#9e9e9e" : "#000000de";
    }
  }

  if (playing && isAutoScroll) {
    words[lastScrolledWord]?.scrollIntoView({ behavior: "smooth", block: "center" });
  }
};

/**
 * Extracts text content from a part object that may be in various formats.
 * Handles string, object with text property, nested objects, and arrays.
 *
 * @param part - The part to extract text from (can be string, object, or array)
 * @returns The extracted text as a string
 */
export const extractNormalizedText = (part: any): string => {
  if (typeof part === "string") return part;
  if (Array.isArray(part)) {
    return part.map(extractNormalizedText).join("");
  }
  if (part && typeof part === "object") {
    // If part.text is an object, extract its text property, otherwise use it as is
    const text = part.text;
    if (typeof text === "string") return text;
    if (text && typeof text === "object" && text.text) return text.text;
    // Fallback: try to stringify if it's an object
    return String(text || part || "");
  }
  return String(part || "");
};

/**
 * Finds the parent element with a valid numeric time-based ID.
 * This is necessary for handling text selection in keyword search mode,
 * where text is wrapped in <mark> elements with "search-*" IDs.
 * We need to traverse up to find the parent <span> with the timestamp ID.
 *
 * @param node - The starting node (typically a text node or element)
 * @returns The element with a valid numeric ID, or null if not found
 */
export const findParentWithTimeId = (node: Node | null): HTMLElement | null => {
  let current = node;
  // Traverse up to 5 levels to find an element with a numeric ID
  for (let i = 0; i < 5 && current; i++) {
    if (current instanceof HTMLElement) {
      const id = current.id;
      // Check if the ID is a valid number (timestamp format)
      if (id && !isNaN(parseFloat(id)) && !id.startsWith("search-")) {
        return current;
      }
    }
    current = current.parentNode;
  }
  return null;
};

/**
 * A 0–1 relevance score as a percentage for the thematic-search score pill,
 * clamped so an out-of-range backend score can never render as e.g. "902%".
 */
export const formatScorePercent = (score: number): string =>
  `${(Math.min(Math.max(Number(score) || 0, 0), 1) * 100).toFixed(1)}%`;
